import { Box, Chip } from '@treneo/shared/ui';

import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';

import type { OrganizationMembershipHistoryRow } from '../../data/types';

export type FormatDateTimeFn = (date: string | Date | null | undefined, fallback: string) => string;

type CreateColumnsParams = {
  t: (key: string) => string;
  formatDateTime: FormatDateTimeFn;
  showUserColumn: boolean;
};

const organizationMembershipColumnFields = {
  userEmail: 'userEmail',
  organizationName: 'organizationName',
  isTrainer: 'isTrainer',
  createdAt: 'createdAt',
  addedByUserEmail: 'addedByUserEmail',
  deletedAt: 'deletedAt',
  deletedByUserEmail: 'deletedByUserEmail',
  isDeleted: 'isDeleted',
  assignmentChangeSource: 'assignmentChangeSource',
  removalChangeSource: 'removalChangeSource'
} as const;

const getDisplayName = (email: string | null | undefined, t: (key: string) => string): string => {
  if (email != null && email.trim() !== '') {
    return email;
  }
  return t('admin.synchronization');
};

const formatMembershipChangeSource = (
  source: number | undefined | null,
  t: (key: string) => string
): string => {
  if (source === 0) {
    return t('admin.membershipSourceSync');
  }
  if (source === 1) {
    return t('admin.membershipSourceInvitationAcceptance');
  }
  if (source === 2) {
    return t('admin.membershipSourceAdminPanel');
  }
  return '-';
};

type MembershipRoleCellProps = Readonly<{
  params: GridRenderCellParams<OrganizationMembershipHistoryRow, boolean>;
  t: (key: string) => string;
}>;

const MembershipRoleCell = ({ params, t }: MembershipRoleCellProps) => {
  const isTrainer = params.value;
  const label = isTrainer ? t('admin.roleTrainer') : t('admin.roleUser');
  const color = isTrainer ? 'primary' : 'default';
  return (
    <Box display='flex' justifyContent='center' alignItems='center' width='100%' height='100%'>
      <Chip label={label} color={color} size='small' />
    </Box>
  );
};

type MembershipStatusCellProps = Readonly<{
  params: GridRenderCellParams<OrganizationMembershipHistoryRow, boolean>;
  t: (key: string) => string;
}>;

const MembershipStatusCell = ({ params, t }: MembershipStatusCellProps) => {
  const isDeleted = params.value;
  const label = isDeleted ? t('admin.deleted') : t('admin.active');
  const color = isDeleted ? 'error' : 'success';
  return (
    <Box display='flex' justifyContent='center' alignItems='center' width='100%' height='100%'>
      <Chip label={label} color={color} size='small' />
    </Box>
  );
};

type MembershipAssignmentSourceCellProps = Readonly<{
  params: GridRenderCellParams<OrganizationMembershipHistoryRow, number | undefined>;
  t: (key: string) => string;
}>;

const MembershipAssignmentSourceCell = ({ params, t }: MembershipAssignmentSourceCellProps) => {
  const source = params.value;
  const label = formatMembershipChangeSource(source, t);
  return (
    <Box display='flex' justifyContent='center' alignItems='center' width='100%' height='100%'>
      <Chip label={label} size='small' color='default' />
    </Box>
  );
};

type MembershipRemovalSourceCellProps = Readonly<{
  params: GridRenderCellParams<OrganizationMembershipHistoryRow>;
  t: (key: string) => string;
}>;

const MembershipRemovalSourceCell = ({ params, t }: MembershipRemovalSourceCellProps) => {
  const row = params.row;
  if (!row.isDeleted) {
    return <>-</>;
  }
  const label = formatMembershipChangeSource(row.removalChangeSource ?? null, t);
  return (
    <Box display='flex' justifyContent='center' alignItems='center' width='100%' height='100%'>
      <Chip label={label} size='small' color='default' />
    </Box>
  );
};

const createUserEmailColumn = (
  t: (key: string) => string
): GridColDef<OrganizationMembershipHistoryRow> => ({
  field: organizationMembershipColumnFields.userEmail,
  headerName: t('admin.user'),
  width: 250,
  flex: 1,
  minWidth: 200,
  valueGetter: (_value: unknown, row: OrganizationMembershipHistoryRow) => row.userEmail ?? '-'
});

const createOrganizationNameColumn = (
  t: (key: string) => string
): GridColDef<OrganizationMembershipHistoryRow> => ({
  field: organizationMembershipColumnFields.organizationName,
  headerName: t('admin.organization'),
  width: 250,
  flex: 1,
  minWidth: 200,
  valueGetter: (_value: unknown, row: OrganizationMembershipHistoryRow) =>
    row.organizationName ?? '-'
});

const createRoleColumn = (
  t: (key: string) => string
): GridColDef<OrganizationMembershipHistoryRow> => ({
  field: organizationMembershipColumnFields.isTrainer,
  headerName: t('admin.role'),
  width: 120,
  align: 'center',
  headerAlign: 'center',
  renderCell: params => <MembershipRoleCell params={params} t={t} />
});

const createAddedAtColumn = (
  t: (key: string) => string,
  formatDateTime: FormatDateTimeFn
): GridColDef<OrganizationMembershipHistoryRow> => ({
  field: organizationMembershipColumnFields.createdAt,
  headerName: t('admin.assignedAt'),
  width: 180,
  sortable: true,
  filterable: true,
  valueFormatter: (value: string | Date | null | undefined) => formatDateTime(value, '-')
});

const createAddedByColumn = (
  t: (key: string) => string
): GridColDef<OrganizationMembershipHistoryRow> => ({
  field: organizationMembershipColumnFields.addedByUserEmail,
  headerName: t('admin.assignedBy'),
  width: 200,
  flex: 1,
  minWidth: 150,
  valueGetter: (_value: unknown, row: OrganizationMembershipHistoryRow) =>
    getDisplayName(row.addedByUserEmail ?? null, t)
});

const createDeletedAtColumn = (
  t: (key: string) => string,
  formatDateTime: FormatDateTimeFn
): GridColDef<OrganizationMembershipHistoryRow> => ({
  field: organizationMembershipColumnFields.deletedAt,
  headerName: t('admin.deletedAt'),
  width: 180,
  sortable: true,
  valueFormatter: (value: string | Date | null | undefined) => formatDateTime(value, '-')
});

const createDeletedByColumn = (
  t: (key: string) => string
): GridColDef<OrganizationMembershipHistoryRow> => ({
  field: organizationMembershipColumnFields.deletedByUserEmail,
  headerName: t('admin.deletedBy'),
  width: 200,
  flex: 1,
  minWidth: 150,
  valueGetter: (_value: unknown, row: OrganizationMembershipHistoryRow) => {
    if (row.isDeleted) {
      return getDisplayName(row.deletedByUserEmail ?? null, t);
    }
    return '-';
  }
});

const createStatusColumn = (
  t: (key: string) => string
): GridColDef<OrganizationMembershipHistoryRow> => ({
  field: organizationMembershipColumnFields.isDeleted,
  headerName: t('admin.status'),
  width: 120,
  align: 'center',
  headerAlign: 'center',
  renderCell: params => <MembershipStatusCell params={params} t={t} />
});

const createAssignmentSourceColumn = (
  t: (key: string) => string
): GridColDef<OrganizationMembershipHistoryRow> => ({
  field: organizationMembershipColumnFields.assignmentChangeSource,
  headerName: t('admin.assignmentSource'),
  width: 160,
  align: 'center',
  headerAlign: 'center',
  renderCell: params => <MembershipAssignmentSourceCell params={params} t={t} />
});

const createRemovalSourceColumn = (
  t: (key: string) => string
): GridColDef<OrganizationMembershipHistoryRow> => ({
  field: organizationMembershipColumnFields.removalChangeSource,
  headerName: t('admin.revocationSource'),
  width: 160,
  align: 'center',
  headerAlign: 'center',
  renderCell: params => <MembershipRemovalSourceCell params={params} t={t} />
});

export const createOrganizationMembershipHistoryColumns = ({
  t,
  formatDateTime,
  showUserColumn
}: CreateColumnsParams): GridColDef<OrganizationMembershipHistoryRow>[] => {
  const columns: GridColDef<OrganizationMembershipHistoryRow>[] = [];
  if (showUserColumn) {
    columns.push(createUserEmailColumn(t));
  } else {
    columns.push(createOrganizationNameColumn(t));
  }
  columns.push(
    createRoleColumn(t),
    createAddedAtColumn(t, formatDateTime),
    createAddedByColumn(t),
    createStatusColumn(t),
    createDeletedAtColumn(t, formatDateTime),
    createDeletedByColumn(t),
    createAssignmentSourceColumn(t),
    createRemovalSourceColumn(t)
  );
  return columns;
};
